import React, { PureComponent, Children } from 'react';
import Swipeable from 'react-swipeable';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { withAppState } from '@ynap/immutables-provider';

const bem = new BEMHelper('Carousel');

const componentPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.func]);

export class Carousel extends PureComponent {
    static propTypes = {
        defaultIndex: PropTypes.number,
        slidesToShow: PropTypes.number,
        slidesToScroll: PropTypes.number,
        transition: PropTypes.string,
        prevComponent: componentPropType,
        nextComponent: componentPropType,
        pageComponent: componentPropType,
        classNames: PropTypes.shape({
            prev: PropTypes.string,
            next: PropTypes.string,
            container: PropTypes.string,
            trackClass: PropTypes.string,
            trackModifier: PropTypes.string,
            slideModifier: PropTypes.string,
            slide: PropTypes.string,
            slideClass: PropTypes.string,
            controlsModifier: PropTypes.string,
            controlsClass: PropTypes.string,
        }),
        children: PropTypes.node,
        onNextClick: PropTypes.func,
        onPrevClick: PropTypes.func,
        appState: PropTypes.shape({
            language: PropTypes.string,
            dir: PropTypes.string,
        }),
        useDefaultCarouselIndex: PropTypes.bool,
        offset: PropTypes.number,
    };

    static defaultProps = {
        defaultIndex: 0,
        slidesToShow: 1,
        slidesToScroll: 1,
        prevComponent: 'button',
        nextComponent: 'button',
        pageComponent: 'button',
        transition: 'transform 0.3s ease-out',
        appState: {
            language: '',
            dir: 'ltr',
        },
        useDefaultCarouselIndex: true,
    };

    constructor(props) {
        super(props);
        const { children, defaultIndex } = this.props;
        const currentIndex = defaultIndex;
        const slides = Children.toArray(children);
        this.state = { slides, currentIndex, delta: 0, direction: 'right' };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { useDefaultCarouselIndex } = this.props;
        const { children } = nextProps;
        const slides = Children.toArray(children);
        this.setState({ slides, ...(useDefaultCarouselIndex ? { currentIndex: nextProps.defaultIndex } : {}) });
    }

    slideTo(index) {
        const {
            appState: { dir },
            slidesToShow,
        } = this.props;
        const { slides } = this.state;
        const currentIndex = Math.max(0, Math.min(index, slides.length - slidesToShow));
        const isRTL = dir === 'rtl';

        if (currentIndex === 0) {
            const direction = isRTL ? 'left' : 'right';
            this.setState({ currentIndex, delta: 0, direction });
        } else if (currentIndex === slides.length - slidesToShow) {
            const direction = isRTL ? 'right' : 'left';
            this.setState({ currentIndex, delta: 0, direction });
        } else {
            const direction = isRTL ? 'right left' : 'left right';
            this.setState({ currentIndex, delta: 0, direction });
        }
    }

    prev() {
        const { onPrevClick, slidesToScroll } = this.props;
        const { currentIndex } = this.state;
        onPrevClick && onPrevClick(currentIndex);
        this.slideTo(Math.ceil(currentIndex - slidesToScroll));
    }

    next() {
        const { onNextClick, slidesToScroll } = this.props;
        const { currentIndex } = this.state;
        onNextClick && onNextClick(currentIndex);
        this.slideTo(Math.floor(currentIndex + slidesToScroll));
    }

    handlePrevClick = () => {
        this.prev();
    };

    handleNextClick = () => {
        this.next();
    };

    handleSwipingLeft = /* istanbul ignore next */ (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }

        const state = {
            delta: -addResistance(delta),
            trackWidth: this.trackRef.offsetWidth,
        };

        this.setState(state);
    };

    handleSwipingRight = /* istanbul ignore next */ (event, delta) => {
        if (event.cancelable) {
            event.preventDefault();
        }

        this.setState({
            delta: addResistance(delta),
            trackWidth: this.trackRef.offsetWidth,
        });
    };

    handleSwiped = /* istanbul ignore next */ (event, deltaX, deltaY, isFlick, velocity) => {
        const { appState, slidesToScroll, slidesToShow } = this.props;
        const { currentIndex } = this.state;
        let nextIndex = currentIndex;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            const shiftIndex = slidesToShow % 1 === 0 ? slidesToScroll : Math.min(slidesToScroll, Math.ceil(velocity * slidesToScroll));
            const direction = appState.dir === 'rtl' ? 1 : -1;

            if (deltaX < 0) {
                nextIndex = Math.ceil(nextIndex + shiftIndex * direction);
            } else if (deltaX > 0) {
                nextIndex = Math.floor(nextIndex - shiftIndex * direction);
            }
        }

        return this.slideTo(nextIndex);
    };

    render() {
        const {
            appState,
            classNames = {},
            nextComponent: NextComponent,
            offset = 0,
            pageComponent: PageComponent,
            prevComponent: PrevComponent,
            slidesToScroll,
            slidesToShow,
            transition,
        } = this.props;
        const { currentIndex, delta, direction, slides, trackWidth } = this.state;

        const {
            container,
            controlsClass,
            controlsModifier,
            next = null,
            prev = null,
            slideClass,
            slideModifier,
            trackClass,
            trackModifier,
            viewportClass,
        } = classNames;

        const isLastWindow = currentIndex >= slides.length - slidesToShow;
        const isRTL = appState.dir === 'rtl';
        const translatePercentage = (100 / slides.length) * currentIndex * (isRTL ? 1 : -1);
        // The offset is to prevent the last slide right padding from being displayed (left padding in case of rtl)
        const translateValueWithOffset = offset && isLastWindow ? `calc(${translatePercentage}% ${isRTL ? '-' : '+'} ${offset}px)` : `${translatePercentage}%`;
        const trackStyle = {
            width: `${(slides.length / slidesToShow) * 100}%`,
            transition: delta === 0 ? transition : 'none',
            transform: (() => {
                let transformValue;

                if (isRTL) {
                    if (delta === 0) {
                        transformValue = `translateX(${translateValueWithOffset})`;
                    } else {
                        transformValue = `translateX(${delta + (trackWidth / slides.length) * currentIndex}px)`;
                    }
                }

                if (delta === 0) {
                    transformValue = `translateX(${translateValueWithOffset})`;
                } else {
                    transformValue = `translateX(${delta - (trackWidth / slides.length) * currentIndex}px)`;
                }

                return transformValue;
            })(),
        };
        const slideStyle = { width: `${100 / slides.length}%` };

        const numPages = Math.ceil(slides.length / slidesToScroll);
        const isFirstWindow = currentIndex <= 0;
        const shouldShowControls = !(isFirstWindow && isLastWindow);

        return (
            <div aria-roledescription="carousel" className={bem(null, null, [container, direction])} ref={this.containerRef}>
                {shouldShowControls && (
                    <div className={bem('controls', controlsModifier, controlsClass)} role="group">
                        {PrevComponent && (
                            <PrevComponent
                                aria-disabled={isFirstWindow}
                                aria-label="Previous slide"
                                className={bem('control', ['prev', controlsModifier])}
                                disabled={isFirstWindow}
                                onClick={this.handlePrevClick}
                                type="button"
                            >
                                <i aria-hidden="true" className={bem('arrow', ['prev', controlsModifier], prev)}></i>
                            </PrevComponent>
                        )}
                        {NextComponent && (
                            <NextComponent
                                aria-disabled={isLastWindow}
                                aria-label="Next slide"
                                className={bem('control', ['next', controlsModifier])}
                                disabled={isLastWindow}
                                onClick={this.handleNextClick}
                                type="button"
                            >
                                <i aria-hidden="true" className={bem('arrow', ['next', controlsModifier], next)}></i>
                            </NextComponent>
                        )}
                    </div>
                )}
                <Swipeable
                    className={bem('viewport', null, viewportClass)}
                    onSwiped={this.handleSwiped}
                    onSwipingLeft={this.handleSwipingLeft}
                    onSwipingRight={this.handleSwipingRight}
                    role="region"
                >
                    <ul
                        className={bem('track', trackModifier, trackClass)}
                        style={trackStyle}
                        ref={(node) => {
                            this.trackRef = node;
                        }}
                    >
                        {slides.map((slide, i) => (
                            <li key={i} aria-roledescription="slide" className={bem('slide', slideModifier, slideClass)} style={slideStyle}>
                                {slide}
                            </li>
                        ))}
                    </ul>
                </Swipeable>
                {PageComponent && (
                    <ul className={bem('pagination')} role="group">
                        {Array(numPages)
                            .fill(undefined)
                            .map((_, i) => (
                                <li key={i}>
                                    <PageComponent
                                        className={bem('page')}
                                        disabled={i === Math.ceil(currentIndex / slidesToScroll)}
                                        onClick={() => this.slideTo(i * slidesToScroll)}
                                        type="button"
                                    >
                                        {i + 1}
                                    </PageComponent>
                                </li>
                            ))}
                    </ul>
                )}
            </div>
        );
    }
}

/* istanbul ignore next */
function addResistance(delta) {
    return delta * (1 - parseInt(Math.sqrt(delta ** 2), 10) / 1000);
}

export default withAppState()(Carousel);
